import * as RadixDialog from '@radix-ui/react-dialog';
import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { cn } from '@shared/lib/cn';
import { Button } from './Button';
import { IconButton } from './IconButton';

/**
 * Radix only returns focus to a Dialog.Trigger; our dialogs are controlled (no Trigger), so focus fell to <body> on
 * close. Remember what had focus when the dialog opened (layout effect: before Radix moves focus inside) and go back
 * to it, or to <main> when that element is gone. A caller's onCloseAutoFocus that calls preventDefault() wins.
 */
function useReturnFocus(open: boolean, custom?: (event: Event) => void) {
  const opener = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (open && document.activeElement instanceof HTMLElement && document.activeElement !== document.body) {
      opener.current = document.activeElement;
    }
  }, [open]);
  return (event: Event) => {
    custom?.(event);
    if (event.defaultPrevented) return;
    event.preventDefault();
    const target = opener.current?.isConnected ? opener.current : document.getElementById('main');
    opener.current = null;
    target?.focus({ preventScroll: true });
  };
}

const WIDTHS = { sm: 'sm:max-w-[25rem]', md: 'sm:max-w-[35rem]', lg: 'sm:max-w-[45rem]' } as const;

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: keyof typeof WIDTHS;
  /** false → clicking the scrim does not close (use for dirty forms and confirmations). */
  dismissOnOutside?: boolean;
  role?: 'dialog' | 'alertdialog';
  onOpenAutoFocus?: (event: Event) => void;
  /** Where focus goes on close; call event.preventDefault() when the trigger no longer exists. */
  onCloseAutoFocus?: (event: Event) => void;
  className?: string;
}

/**
 * Centred modal (design system §4.17): radius xl, shadow-3, full screen below sm.
 * Radix traps focus, closes on Escape and returns focus to the trigger.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissOnOutside = true,
  role = 'dialog',
  onOpenAutoFocus,
  onCloseAutoFocus,
  className,
}: DialogProps) {
  const { t } = useTranslation('common');
  const returnFocus = useReturnFocus(open, onCloseAutoFocus);
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-modal bg-surface-overlay" />
        <RadixDialog.Content
          role={role}
          onOpenAutoFocus={onOpenAutoFocus}
          onCloseAutoFocus={returnFocus}
          onInteractOutside={(event) => {
            if (!dismissOnOutside) event.preventDefault();
          }}
          {...(description ? {} : { 'aria-describedby': undefined })}
          className={cn(
            'fixed inset-0 z-modal flex flex-col bg-surface-raised shadow-3 outline-none',
            // Centred with inset-0 + margin:auto — direction-neutral, no transforms.
            'sm:m-auto sm:h-fit sm:max-h-[85dvh] sm:w-[calc(100%-2rem)] sm:rounded-xl',
            WIDTHS[size],
            className,
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-border-subtle px-6 py-4">
            <div className="min-w-0">
              <RadixDialog.Title className="text-xl font-semibold text-heading">{title}</RadixDialog.Title>
              {description ? (
                <RadixDialog.Description className="mt-1 text-sm text-muted">{description}</RadixDialog.Description>
              ) : null}
            </div>
            <RadixDialog.Close asChild>
              <IconButton icon={X} label={t('actions.close')} showTooltip={false} />
            </RadixDialog.Close>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
          {footer ? (
            <div className="flex flex-wrap items-center justify-end gap-3 border-t border-border-subtle px-6 py-4">{footer}</div>
          ) : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

export interface DrawerProps extends Omit<DialogProps, 'size' | 'role'> {
  width?: 'md' | 'lg';
}

/** Detail panel sliding from the end side (right in LTR, left in RTL); bottom sheet on phone. */
export function Drawer({ open, onOpenChange, title, description, children, footer, width = 'md', className }: DrawerProps) {
  const { t } = useTranslation('common');
  const returnFocus = useReturnFocus(open);
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-drawer bg-surface-overlay" />
        <RadixDialog.Content
          onCloseAutoFocus={returnFocus}
          {...(description ? {} : { 'aria-describedby': undefined })}
          className={cn(
            'fixed z-drawer flex flex-col bg-surface-raised shadow-3 outline-none',
            'inset-x-0 bottom-0 max-h-[85dvh] rounded-t-xl pb-[env(safe-area-inset-bottom)]',
            'md:inset-y-0 md:end-0 md:start-auto md:max-h-none md:rounded-none md:pb-0',
            width === 'lg' ? 'md:w-[35rem]' : 'md:w-[25rem]',
            className,
          )}
        >
          <div aria-hidden="true" className="mx-auto mt-2 h-1 w-10 rounded-full bg-border md:hidden" />
          <div className="flex items-start justify-between gap-4 border-b border-border-subtle px-6 py-4">
            <div className="min-w-0">
              <RadixDialog.Title className="text-xl font-semibold text-heading">{title}</RadixDialog.Title>
              {description ? (
                <RadixDialog.Description className="mt-1 text-sm text-muted">{description}</RadixDialog.Description>
              ) : null}
            </div>
            <RadixDialog.Close asChild>
              <IconButton icon={X} label={t('actions.close')} showTooltip={false} />
            </RadixDialog.Close>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
          {footer ? <div className="flex items-center justify-end gap-3 border-t border-border-subtle px-6 py-4">{footer}</div> : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  /** What will happen, to whom, and whether it is reversible. */
  description: ReactNode;
  /** Verb label ("Remove role"), never "OK". */
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCloseAutoFocus?: (event: Event) => void;
  children?: ReactNode;
}

/** Destructive confirmation (design system §5.2): cancel is focused first, no scrim dismissal. */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  destructive = true,
  loading,
  onConfirm,
  onCloseAutoFocus,
  children,
}: ConfirmDialogProps) {
  const { t } = useTranslation('common');
  const cancelRef = useRef<HTMLButtonElement>(null);
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      size="sm"
      role="alertdialog"
      dismissOnOutside={false}
      onOpenAutoFocus={(event) => {
        event.preventDefault();
        cancelRef.current?.focus();
      }}
      onCloseAutoFocus={onCloseAutoFocus}
      footer={
        <>
          <Button ref={cancelRef} variant="secondary" onClick={() => onOpenChange(false)}>
            {cancelLabel ?? t('actions.cancel')}
          </Button>
          <Button variant={destructive ? 'danger' : 'primary'} loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
    </Dialog>
  );
}
